import React, { useMemo, useState } from 'react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';

function TrendsView({ data, darkMode }) {
  const dailyStats = useMemo(() => data?.daily_stats || [], [data?.daily_stats]);
  const [dateFilter, setDateFilter] = useState('overall'); // 'overall' | 'range'
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');

  const siteHealth = (s) => s?.site_stats?.avg_health_ratio ?? s?.health_ratio;

  const filteredDaily = useMemo(() => {
    if (!dailyStats.length) return [];
    if (dateFilter === 'overall' || !startDate || !endDate) return dailyStats;
    return dailyStats.filter(
      (d) => d.date >= startDate && d.date <= endDate
    );
  }, [dailyStats, dateFilter, startDate, endDate]);

  const chartData = useMemo(
    () =>
      filteredDaily.map((d) => ({
        date: d.date,
        'Avg health (%)': d.avg_health_ratio,
        site_count: d.site_count,
      })),
    [filteredDaily]
  );

  const hasMultiDay = data?.overall_stats?.is_multi_day && dailyStats.length > 0;

  if (!data || !data.sites) {
    return (
      <div className={`rounded-lg shadow-sm p-6 ${darkMode ? 'bg-gray-800' : 'bg-white'}`}>
        <h2 className={`text-xl font-bold mb-2 ${darkMode ? 'text-white' : 'text-gray-800'}`}>Performance Trends</h2>
        <p className={`mb-6 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>Track performance over time</p>
        <div className="text-center py-12">
          <p className={darkMode ? 'text-gray-400' : 'text-gray-500'}>No data available. Please upload a file first.</p>
        </div>
      </div>
    );
  }

  return (
    <div className={`rounded-lg shadow-sm p-6 ${darkMode ? 'bg-gray-800' : 'bg-white'}`}>
      <h2 className={`text-xl font-bold mb-2 ${darkMode ? 'text-white' : 'text-gray-800'}`}>Performance Trends</h2>
      <p className={`mb-6 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
        Inverter health over time. Filter by date or view overall.
      </p>

      {/* Date filter (only when we have multi-day daily_stats) */}
      {hasMultiDay && (
        <div className={`mb-6 p-4 rounded-lg border ${darkMode ? 'bg-gray-700/50 border-gray-600' : 'bg-gray-50 border-gray-200'}`}>
          <h3 className={`text-sm font-semibold mb-3 ${darkMode ? 'text-gray-200' : 'text-gray-700'}`}>Filter by date</h3>
          <div className="flex flex-wrap items-center gap-4">
            <label className="flex items-center gap-2">
              <input
                type="radio"
                name="trendFilter"
                checked={dateFilter === 'overall'}
                onChange={() => setDateFilter('overall')}
                className="text-green-600"
              />
              <span className={darkMode ? 'text-gray-300' : 'text-gray-700'}>Overall (all {dailyStats.length} days)</span>
            </label>
            <label className="flex items-center gap-2">
              <input
                type="radio"
                name="trendFilter"
                checked={dateFilter === 'range'}
                onChange={() => setDateFilter('range')}
                className="text-green-600"
              />
              <span className={darkMode ? 'text-gray-300' : 'text-gray-700'}>Date range</span>
            </label>
            {dateFilter === 'range' && (
              <>
                <input
                  type="date"
                  value={startDate}
                  onChange={(e) => setStartDate(e.target.value)}
                  className={`px-2 py-1 rounded border text-sm ${darkMode ? 'bg-gray-600 border-gray-500 text-white' : 'bg-white border-gray-300'}`}
                />
                <span className={darkMode ? 'text-gray-400' : 'text-gray-500'}>to</span>
                <input
                  type="date"
                  value={endDate}
                  onChange={(e) => setEndDate(e.target.value)}
                  className={`px-2 py-1 rounded border text-sm ${darkMode ? 'bg-gray-600 border-gray-500 text-white' : 'bg-white border-gray-300'}`}
                />
              </>
            )}
          </div>
        </div>
      )}

      {/* Daily health graph (multi-day only) */}
      {hasMultiDay && chartData.length > 0 && (
        <div className={`mb-8 rounded-lg border p-4 ${darkMode ? 'bg-gray-700/30 border-gray-600' : 'bg-white border-gray-200'}`}>
          <h3 className={`font-semibold mb-3 ${darkMode ? 'text-gray-200' : 'text-gray-700'}`}>
            Inverter health by day (average across all sites)
          </h3>
          <p className={`text-xs mb-4 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
            Each point = that day’s average health across all sites. Same period as your upload ({data.overall_stats?.data_date}).
          </p>
          <div className="h-80">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={chartData} margin={{ top: 10, right: 20, left: 10, bottom: 30 }}>
                <CartesianGrid strokeDasharray="3 3" stroke={darkMode ? '#374151' : '#e5e7eb'} />
                <XAxis dataKey="date" tick={{ fontSize: 11 }} angle={-45} textAnchor="end" height={50} stroke={darkMode ? '#9ca3af' : '#6b7280'} />
                <YAxis domain={[0, 100]} tick={{ fontSize: 11 }} width={40} stroke={darkMode ? '#9ca3af' : '#6b7280'} />
                <Tooltip
                  content={({ active, payload, label }) => {
                    if (active && payload && payload.length) {
                      return (
                        <div className={`p-3 rounded shadow-lg border ${darkMode ? 'bg-gray-700 border-gray-600' : 'bg-white border-gray-200'}`}>
                          <p className="font-semibold">{label}</p>
                          <p>{payload[0]?.value != null ? `${Number(payload[0].value).toFixed(2)}%` : 'N/A'} avg health</p>
                        </div>
                      );
                    }
                    return null;
                  }}
                />
                <Line type="monotone" dataKey="Avg health (%)" stroke="#22c55e" strokeWidth={2} dot={{ r: 2 }} name="Avg health (%)" />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>
      )}

      {data.overall_stats?.is_multi_day && !dailyStats.length && (
        <p className={`mb-6 text-sm ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
          Daily trend graph is available after loading full data (open Overview/Analytics once).
        </p>
      )}

      {/* Trend insights (site-level) */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
        <div className={`border rounded-lg p-4 ${darkMode ? 'border-gray-700' : 'border-gray-200'}`}>
          <h3 className={`font-semibold mb-3 flex items-center ${darkMode ? 'text-gray-200' : 'text-gray-700'}`}>
            <span className="w-3 h-3 bg-green-500 rounded-full mr-2"></span>
            Top Performers
          </h3>
          <div className="space-y-2">
            {data.sites
              .filter((s) => siteHealth(s) != null)
              .sort((a, b) => (siteHealth(b) || 0) - (siteHealth(a) || 0))
              .slice(0, 5)
              .map((site, idx) => (
                <div key={idx} className="flex items-center justify-between text-sm">
                  <span className={`truncate flex-1 ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>{site.site_name}</span>
                  <span className={`font-medium ml-2 ${darkMode ? 'text-green-400' : 'text-green-600'}`}>{siteHealth(site)?.toFixed(1)}%</span>
                </div>
              ))}
          </div>
        </div>
        <div className={`border rounded-lg p-4 ${darkMode ? 'border-gray-700' : 'border-gray-200'}`}>
          <h3 className={`font-semibold mb-3 flex items-center ${darkMode ? 'text-gray-200' : 'text-gray-700'}`}>
            <span className="w-3 h-3 bg-red-500 rounded-full mr-2"></span>
            Needs Attention
          </h3>
          <div className="space-y-2">
            {data.sites
              .filter((s) => siteHealth(s) != null)
              .sort((a, b) => (siteHealth(a) || 0) - (siteHealth(b) || 0))
              .slice(0, 5)
              .map((site, idx) => (
                <div key={idx} className="flex items-center justify-between text-sm">
                  <span className={`truncate flex-1 ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>{site.site_name}</span>
                  <span className={`font-medium ml-2 ${darkMode ? 'text-red-400' : 'text-red-600'}`}>{siteHealth(site)?.toFixed(1)}%</span>
                </div>
              ))}
          </div>
        </div>
      </div>

      {/* Health distribution */}
      <h3 className={`font-semibold mb-3 ${darkMode ? 'text-gray-200' : 'text-gray-700'}`}>Health distribution (sites)</h3>
      <div className="flex items-center gap-4 mb-2">
        <div className={`flex-1 rounded-full h-4 overflow-hidden ${darkMode ? 'bg-gray-700' : 'bg-gray-200'}`}>
          <div className="h-full flex">
            <div className="bg-green-500" style={{ width: `${(data.sites.filter((s) => (siteHealth(s) || 0) >= 90).length / data.sites.length) * 100}%` }}></div>
            <div className="bg-yellow-500" style={{ width: `${(data.sites.filter((s) => (siteHealth(s) || 0) >= 70 && (siteHealth(s) || 0) < 90).length / data.sites.length) * 100}%` }}></div>
            <div className="bg-red-500" style={{ width: `${(data.sites.filter((s) => (siteHealth(s) || 0) < 70).length / data.sites.length) * 100}%` }}></div>
          </div>
        </div>
      </div>
      <div className={`flex items-center justify-center gap-6 text-sm ${darkMode ? 'text-gray-300' : ''}`}>
        <span className="flex items-center"><span className="w-3 h-3 bg-green-500 rounded mr-1"></span> Good ({data.sites.filter((s) => (siteHealth(s) || 0) >= 90).length})</span>
        <span className="flex items-center"><span className="w-3 h-3 bg-yellow-500 rounded mr-1"></span> Fair ({data.sites.filter((s) => (siteHealth(s) || 0) >= 70 && (siteHealth(s) || 0) < 90).length})</span>
        <span className="flex items-center"><span className="w-3 h-3 bg-red-500 rounded mr-1"></span> Poor ({data.sites.filter((s) => (siteHealth(s) || 0) < 70).length})</span>
      </div>
    </div>
  );
}

export default TrendsView;
